import {HttpCodes} from "../generics/httpCodes";

export interface IHttpMethodStrategy {
  execute(
    url: string,
    method: string,
    body: any,
    headers: Headers
  ): Promise<any>;
}

export class NonBodyHttpMethodStrategy implements IHttpMethodStrategy {
  async nonBodyMethod(
    url: string = "",
    method: string,
    headers: Headers
  ): Promise<Response> {
    const requestInit: RequestInit = { headers, method };
    return await fetch(url, requestInit);
  }

  async execute(
    url: string,
    method: string,
    body: any,
    headers: Headers
  ): Promise<any> {
    const response: Response = await this.nonBodyMethod(url, method, headers);
    if (!response.ok) {
      let errorBody
      try {
        errorBody = await response.json()
      } catch (error) {
        errorBody = response.statusText
      }

      return Promise.reject({
        status: response.status,
        statusText: errorBody,
        response,
        method: method
      })
    }
    try {
       return {
        status: response.status,
        data: await response.json(),
        method: method,
        statusText: response.statusText
      }
    } catch (e) {
       return {
        status: response.status,
        method: method,
        statusText: response.statusText,
        data: null
      }
    }
  }
}

export class NonBodyBlobHttpMethodStrategy implements IHttpMethodStrategy {
  async nonBodyMethod(
    url: string = "",
    method: string,
    headers: Headers
  ): Promise<Response> {
    const requestInit: RequestInit = { headers, method };
    return await fetch(url, requestInit);
  }

  async execute(
    url: string,
    method: string,
    body: any,
    headers: Headers
  ): Promise<any> {
    const response: Response = await this.nonBodyMethod(url, "GET", headers);
    if (!response.ok) {
       return Promise.reject({
        status: response.status,
        statusText: response.statusText,
        response,
        method: method
      });
    }
    return {
      status: response.status,
      method: method,
      statusText: response.statusText,
      data: await response.blob()
    }
  }
}

export class BlobDownloadHttpMethodStrategy extends NonBodyBlobHttpMethodStrategy {
  async execute(
    url: string,
    method: string,
    body: any,
    headers: Headers
  ): Promise<any> {
    const response: Response = await this.nonBodyMethod(url, "GET", headers);
    if (!response.ok) {
      return Promise.reject({
        status: response.status,
        statusText: response.statusText,
        response,
        method: method
      });
    }
    let fileName: string = body?.fileName || null;
    fileName =
      response.headers.get("Content-Disposition")?.split("filename=")[1] ??
      fileName; // this allows you to specify a filename in the backend
    const strippedFileName: string = fileName.replace(/"/g, "");
    url = window.URL.createObjectURL(
      new Blob([await response.blob()], { type: "application/octet-stream" })
    );
    const link: HTMLAnchorElement = document.createElement("a");
    link.href = url;
    link.setAttribute("download", strippedFileName);
    document.body.appendChild(link);
    link.click();
    return {
      status: response.status,
      method: method,
      statusText: response.statusText,
      data: null
    }
  }
}

export class BodyHttpMethodStrategy implements IHttpMethodStrategy {
  async hasBodyMethod(
    url: string = "",
    method: string,
    body: any,
    headers: Headers
  ): Promise<Response> {
    const requestInit: RequestInit = {
      headers: headers,
      method,
      body: body instanceof FormData ? body : JSON.stringify(body),
    };
    return await fetch(url, requestInit);
  }

  async execute(
    url: string,
    method: string,
    body: any,
    headers: Headers
  ): Promise<any> {
    headers.set("Content-Type", "application/json");
    if (body instanceof FormData) {
      headers.delete("Content-Type");
    }
    const response: Response = await this.hasBodyMethod(
      url,
      method,
      body,
      headers
    );
    if (!response.ok) {
    let errorBody
      try {
        errorBody = await response.json()
      } catch (error) {
        errorBody = response.statusText
      }

      return Promise.reject({
        status: response.status,
        statusText: errorBody,
        response,
        method: method
      })
    }
    if (response.status === HttpCodes.HTTP_NO_CONTENT) {
      return {
        status: response.status,
        data: null,
        method: method,
        statusText: response.statusText
      };
    }
     return {
      status: response.status,
      data: await response.json(),
      method: method,
      statusText: response.statusText
    }
  }
}
